import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

/**
 * Drill-through coordination for the SPA.
 *
 * - `<DrillthroughListener>` at the app shell listens for
 *   `postMessage({type:'canopy.drill', href})` from kiosk iframes and
 *   navigates the SPA to the target (stripping the /reporting/app/
 *   prefix so we stay client-side).
 * - Each drill records a breadcrumb in sessionStorage; the detail pages
 *   render `<DrillBreadcrumb />` to show where the user came from and a
 *   one-click return.
 *
 * The full URL includes any resolved params as query string so the
 * target can bind them (e.g. device_id from the clicked table row).
 */
const BREADCRUMB_KEY = 'canopy.drill.breadcrumb';

interface Breadcrumb {
  fromLabel: string;
  fromHref: string;
  at: number; // epoch ms — expires after 10 minutes
}

export function DrillthroughListener(): null {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    function onMessage(e: MessageEvent): void {
      const data = e.data as { type?: string; href?: string } | undefined;
      if (!data || data.type !== 'canopy.drill' || !data.href) return;
      // Restrict to our own origin (same site, since the kiosk iframe is
      // served from the same tenant app). Ignore cross-origin drills.
      try {
        const parsed = new URL(data.href, window.location.origin);
        if (parsed.origin !== window.location.origin) return;
        // Record the current page as the "from" breadcrumb so the drill
        // target can offer a return link.
        const label = describeCurrent(location.pathname);
        const crumb: Breadcrumb = {
          fromLabel: label,
          fromHref: location.pathname + location.search,
          at: Date.now(),
        };
        try {
          sessionStorage.setItem(BREADCRUMB_KEY, JSON.stringify(crumb));
        } catch {
          /* noop */
        }
        // Strip the /reporting/app prefix so React Router resolves it.
        const target = parsed.pathname.replace(/^\/reporting\/app/, '') + parsed.search;
        navigate(target);
      } catch {
        /* invalid URL — silently ignore */
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate, location.pathname, location.search]);

  return null;
}

function describeCurrent(pathname: string): string {
  if (pathname.startsWith('/templates')) return 'Templates';
  if (pathname.startsWith('/reports')) return 'Reports';
  if (pathname.startsWith('/insights')) return 'Dashboard';
  return 'Previous page';
}

/**
 * Renders a small "← back to X" pill at the top of drill-through
 * destination pages. Reads the breadcrumb from sessionStorage; expires
 * after 10 minutes so stale crumbs don't reappear after a lunch break.
 */
export function DrillBreadcrumb(): JSX.Element | null {
  const [crumb, setCrumb] = useState<Breadcrumb | null>(() => readCrumb());
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // Hide the breadcrumb on plain navigation (no drill params).
  useEffect(() => {
    const c = readCrumb();
    setCrumb(c);
  }, [params]);

  if (!crumb) return null;

  return (
    <div className="drill-breadcrumb">
      <button
        type="button"
        className="drill-breadcrumb-back"
        onClick={() => {
          try {
            sessionStorage.removeItem(BREADCRUMB_KEY);
          } catch {
            /* noop */
          }
          navigate(crumb.fromHref);
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 12L6 8l4-4" />
        </svg>
        Back to {crumb.fromLabel}
      </button>
      <span className="drill-breadcrumb-meta text-dim text-xs">
        · drilled {formatRel(crumb.at)}
      </span>
      <button
        type="button"
        className="btn-icon"
        onClick={() => {
          try {
            sessionStorage.removeItem(BREADCRUMB_KEY);
          } catch {
            /* noop */
          }
          setCrumb(null);
        }}
        title="Dismiss breadcrumb"
        aria-label="Dismiss breadcrumb"
      >
        ×
      </button>
    </div>
  );
}

function readCrumb(): Breadcrumb | null {
  try {
    const raw = sessionStorage.getItem(BREADCRUMB_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Breadcrumb;
    if (!c.at || Date.now() - c.at > 10 * 60 * 1000) {
      sessionStorage.removeItem(BREADCRUMB_KEY);
      return null;
    }
    return c;
  } catch {
    return null;
  }
}

function formatRel(t: number): string {
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return `${m}m ago`;
}
